'use client';

import { useCallback } from 'react';
import type {
  FormValues,
  FormErrors,
  FormTouched,
  FormInstance,
  StepConfig,
  CustomErrorMessages,
} from '../types';
import { collectCardFormData, clearCardFormData } from '../utils/sections';
import type { UseAddMoreFormReturn } from './useAddMoreForm';
import {
  findAddMoreFieldGroupIndex,
  getAddMoreConfig,
  isAddMoreEnabled,
} from '../utils/addMoreConfig';
import { evaluateConditions } from '../utils/conditions';
import { validateField, scrollToFirstError } from '../utils/validation';

interface UseAddMoreHandlerProps {
  sectionInfo: ReturnType<typeof import('../utils/sections').findSectionForStep> | null;
  currentFormInstanceId: string | null;
  currentStepId: string;
  stepsById: Record<string, StepConfig>;
  values: FormValues;
  completedForms: Record<string, FormInstance[]>;
  setValues: React.Dispatch<React.SetStateAction<FormValues>>;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  setTouched: React.Dispatch<React.SetStateAction<FormTouched>>;
  setCompletedForms: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>;
  setCurrentFormInstanceId: (id: string | null) => void;
  setCurrentStepIndex: (index: number) => void;
  setCurrentStepId: (stepId: string) => void;
  validateCurrentStep: () => boolean;
  currentStep: StepConfig;
  addMoreFormStates?: Record<string, UseAddMoreFormReturn>;
  localStorageKey?: string;
  customErrorMessages?: CustomErrorMessages;
}

/**
 * Hook to handle "Add More" functionality for cards
 */
export const useAddMoreHandler = ({
  sectionInfo,
  currentFormInstanceId,
  currentStepId: _currentStepId,
  currentStep,
  stepsById,
  values,
  completedForms,
  setValues,
  setErrors,
  setTouched,
  setCompletedForms,
  setCurrentFormInstanceId,
  setCurrentStepIndex,
  setCurrentStepId,
  validateCurrentStep,
  addMoreFormStates,
  localStorageKey,
  customErrorMessages,
}: UseAddMoreHandlerProps) => {
  // Get addMore config for current step
  const addMoreConfig = getAddMoreConfig(currentStep);
  const isAddMoreStep = Boolean(addMoreConfig);
  const isEnabled = isAddMoreStep ? isAddMoreEnabled(currentStep, values) : false;
  const addMoreFormState = addMoreConfig ? addMoreFormStates?.[addMoreConfig.groupKey] : null;

  const handleAdd = useCallback(() => {
    // Generic addMore handling
    if (isAddMoreStep && addMoreFormState && isEnabled) {
      if (addMoreConfig) {
        const newErrors: Record<string, string | undefined> = {};
        const newTouched: Record<string, boolean | undefined> = {};
        let hasErrors = false;

        // Get the fieldGroup containing addMore fields
        const fieldGroupIndex = findAddMoreFieldGroupIndex(currentStep, addMoreConfig);
        const fieldGroup = currentStep.fieldGroups?.[fieldGroupIndex];

        if (fieldGroup && fieldGroup.fields) {
          // Validate only fields in the addMore fieldGroup
          for (const field of fieldGroup.fields) {
            // Check if field should be visible
            const shouldShowField = !field.showIf || evaluateConditions(field.showIf, values);
            if (!shouldShowField) continue;

            // Validate only addMore fieldIds
            if (addMoreConfig.fieldIds.includes(field.id)) {
              const value = values[field.id];
              const error = validateField(field, value, {
                completedForms,
                allValues: values,
                editingInstanceId: addMoreFormState?.editingInstanceId ?? currentFormInstanceId,
                addMoreConfig,
                localStorageKey,
                validationInstanceId: addMoreFormState?.originalInstanceData?.instanceId,
                customErrorMessages,
              });
              if (error) {
                newErrors[field.id] = error;
                newTouched[field.id] = true;
                hasErrors = true;
              }
            }
          }
        }

        if (hasErrors) {
          setErrors((prev) => ({ ...prev, ...newErrors }));
          setTouched((prev) => ({ ...prev, ...newTouched }));
          scrollToFirstError(newErrors);
          return; // Don't add if validation fails
        }
      }

      // Use generic form hook's handleAdd method
      addMoreFormState.handleAdd();

      // Don't change step or instance ID, stay on the same step
      return;
    }

    // Default behavior for card-based forms
    if (!sectionInfo?.card || !currentFormInstanceId) return;

    // Validate current step before adding
    if (!validateCurrentStep()) return;

    // Default behavior: Collect form data for current card
    const allFormData = collectCardFormData(sectionInfo.card, stepsById, values);

    // Save as new instance
    const instanceId = `${sectionInfo.card.id}-${Date.now()}`;
    setCompletedForms((prev) => {
      const cardInstances = prev[sectionInfo.card!.groupKey] || [];
      return {
        ...prev,
        [sectionInfo.card!.groupKey]: [
          ...cardInstances,
          {
            instanceId,
            data: allFormData,
            lastStepIndex: sectionInfo.stepIndex,
          },
        ],
      };
    });

    // Clear form values for this card
    const clearedValues = clearCardFormData(sectionInfo.card, stepsById, values);
    setValues(clearedValues);
    setErrors({});
    setTouched({});

    // Generate new instance ID and reset to first step
    const newInstanceId = `${sectionInfo.card.id}-${Date.now()}`;
    setCurrentFormInstanceId(newInstanceId);
    setCurrentStepIndex(0);
    setCurrentStepId(sectionInfo.card.stepIds[0]);
  }, [
    sectionInfo,
    currentFormInstanceId,
    stepsById,
    values,
    setValues,
    setErrors,
    setTouched,
    setCompletedForms,
    setCurrentFormInstanceId,
    setCurrentStepIndex,
    setCurrentStepId,
    validateCurrentStep,
    isAddMoreStep,
    addMoreFormState,
    isEnabled,
    addMoreConfig,
    currentStep,
  ]);

  // Handler for "Add another" button - shows form
  const handleAddAnother = useCallback(() => {
    if (isAddMoreStep && addMoreFormState && isEnabled) {
      addMoreFormState.setShowForm(true);
    }
  }, [isAddMoreStep, addMoreFormState, isEnabled]);

  return { handleAdd, handleAddAnother };
};
